import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelPettyCashMovement,
  createPettyCash,
  createPettyCashCategory,
  createPettyCashExpense,
  createPettyCashLoad,
  fetchPettyCash,
  fetchPettyCashAvailable,
  fetchPettyCashCategories,
  fetchPettyCashMovements,
  updatePettyCash,
  updatePettyCashCategory,
  type CreatedPettyCashMovement,
} from '../api/pettyCash';
import type {
  CreatePettyCashExpenseRequest,
  CreatePettyCashLoadRequest,
  CreatePettyCashRequest,
  ListPettyCashMovementsQuery,
  PettyCash,
  PettyCashAvailable,
  PettyCashCategory,
  PettyCashMovement,
  PettyCashState,
  UpdatePettyCashRequest,
  UpsertPettyCashCategoryRequest,
} from '@deposits/shared';

/** Header state. Short-lived — the available cash reflects Arbox live. */
export function usePettyCash(projectId: number | null, enabled = true) {
  return useQuery<PettyCashState | null>({
    queryKey: ['petty-cash', projectId],
    queryFn: () => fetchPettyCash(projectId),
    enabled,
    staleTime: 0,
  });
}

export function usePettyCashAvailable(id: number | null) {
  return useQuery<PettyCashAvailable>({
    queryKey: ['petty-cash-available', id],
    queryFn: () => fetchPettyCashAvailable(id!),
    enabled: id != null,
    staleTime: 0,
  });
}

export function usePettyCashMovements(id: number | null, q: ListPettyCashMovementsQuery) {
  return useQuery<PettyCashMovement[]>({
    queryKey: ['petty-cash-movements', id, q],
    queryFn: () => fetchPettyCashMovements(id!, q),
    enabled: id != null,
    staleTime: 15_000,
  });
}

/** Everything a movement changes: balance, free receipts, history — and the
 *  other flows that read the same receipts pool. */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['petty-cash'] });
  qc.invalidateQueries({ queryKey: ['petty-cash-available'] });
  qc.invalidateQueries({ queryKey: ['petty-cash-movements'] });
  qc.invalidateQueries({ queryKey: ['register-current'] });
  qc.invalidateQueries({ queryKey: ['receipts-pending'] });
}

export function useSavePettyCash() {
  const qc = useQueryClient();
  return useMutation<
    PettyCash,
    Error,
    { id?: number; create?: CreatePettyCashRequest; update?: UpdatePettyCashRequest }
  >({
    mutationFn: ({ id, create, update }) =>
      id != null ? updatePettyCash(id, update!) : createPettyCash(create!),
    // Don't await the refetch: the caller closes its modal first. Awaiting it
    // swaps the empty state for the main view while the form is still open,
    // which flashes the edit form.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['petty-cash'] });
    },
  });
}

export function useCreatePettyCashLoad() {
  const qc = useQueryClient();
  return useMutation<CreatedPettyCashMovement, Error, { id: number; body: CreatePettyCashLoadRequest }>({
    mutationFn: ({ id, body }) => createPettyCashLoad(id, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreatePettyCashExpense() {
  const qc = useQueryClient();
  return useMutation<CreatedPettyCashMovement, Error, { id: number; body: CreatePettyCashExpenseRequest }>({
    mutationFn: ({ id, body }) => createPettyCashExpense(id, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCancelPettyCashMovement() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => cancelPettyCashMovement(id, reason),
    onSuccess: () => invalidateAll(qc),
  });
}

export function usePettyCashCategories(enabled = true) {
  return useQuery<PettyCashCategory[]>({
    queryKey: ['petty-cash-categories'],
    queryFn: fetchPettyCashCategories,
    enabled,
    staleTime: 60_000,
  });
}

export function useSavePettyCashCategory() {
  const qc = useQueryClient();
  return useMutation<PettyCashCategory, Error, { id?: number; body: UpsertPettyCashCategoryRequest }>({
    mutationFn: ({ id, body }) =>
      id != null ? updatePettyCashCategory(id, body) : createPettyCashCategory(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['petty-cash-categories'] });
      qc.invalidateQueries({ queryKey: ['petty-cash-movements'] });
    },
  });
}
